import { useMemo, useState } from "react";
import { Calculator, Sparkles } from "lucide-react";
import { wheelMembers } from "../lib/poolMembers";
import { currencySymbol, formatCurrency } from "../contexts/FxContext";
import { DEFAULT_CURRENCY } from "../lib/defaultCurrency";

export function ProfitSimulator({ pool }) {
    const [amount, setAmount] = useState("");
    const [breakdown, setBreakdown] = useState([]);
    const [total, setTotal] = useState(0);

    const currency = pool?.currency || DEFAULT_CURRENCY;
    const active = useMemo(() => wheelMembers(pool?.members), [pool?.members]);

    const compute = (raw) => {
        const num = parseFloat(raw || "0");
        if (!active.length || Number.isNaN(num) || num < 0) {
            setBreakdown([]);
            setTotal(0);
            return;
        }
        setBreakdown(
            active.map((m) => ({
                member_id: m.id,
                name: m.name,
                role_label: m.role_label,
                percentage: m.percentage,
                amount: +(num * m.percentage / 100).toFixed(2),
                color: m.color,
                is_owner: m.is_owner,
            })),
        );
        setTotal(num);
    };

    const handleChange = (e) => {
        const v = e.target.value.replace(/[^0-9.]/g, "");
        setAmount(v);
        compute(v);
    };

    const symbol = currencySymbol(currency);
    const hasBreakdown = breakdown.length > 0;

    return (
        <section className="profit-simulator surface p-5 sm:p-6" data-testid="profit-simulator">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <p className="label-eyebrow">Tools</p>
                    <h3 className="font-heading text-xl sm:text-2xl tracking-tight mt-1">Distribution simulator</h3>
                    <p className="mt-1.5 text-xs text-white/45">
                        Preview how a payout splits across {active.length} active member{active.length === 1 ? "" : "s"}.
                    </p>
                </div>
                <div className="profit-simulator__icon shrink-0 rounded-xl border border-white/[0.08] bg-white/[0.03] p-2.5">
                    <Calculator className="h-5 w-5 text-[#00BF0D]/80" />
                </div>
            </div>

            <label className="profit-simulator__input block mt-5">
                <span className="label-eyebrow">Revenue amount</span>
                <div className="mt-2 relative">
                    <span className="absolute inset-y-0 left-3.5 flex items-center text-white/40 font-mono text-lg">{symbol}</span>
                    <input
                        type="text"
                        inputMode="decimal"
                        value={amount}
                        onChange={handleChange}
                        placeholder="50,000"
                        className={`input-dark w-full rounded-xl pr-4 py-3 text-lg font-mono ${symbol.length > 1 ? "pl-10" : "pl-8"}`}
                        data-testid="simulator-amount-input"
                    />
                </div>
            </label>

            {hasBreakdown && (
                <div className="profit-simulator__bar mt-5" data-testid="simulator-split-bar">
                    <div className="flex h-2.5 overflow-hidden rounded-full bg-white/[0.04] ring-1 ring-white/[0.06]">
                        {breakdown.map((b) => (
                            <div
                                key={b.member_id}
                                className="profit-simulator__bar-segment min-w-[2px] transition-[width] duration-300 ease-out"
                                style={{
                                    width: `${b.percentage}%`,
                                    backgroundColor: b.color,
                                    boxShadow: `0 0 10px ${b.color}55`,
                                }}
                                title={`${b.name}: ${b.percentage}%`}
                            />
                        ))}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                        {breakdown.map((b) => (
                            <span key={`legend-${b.member_id}`} className="inline-flex items-center gap-1.5 text-[10px] text-white/45">
                                <span
                                    className="h-2 w-2 rounded-full shrink-0"
                                    style={{ backgroundColor: b.color, boxShadow: `0 0 6px ${b.color}` }}
                                />
                                {b.name} · {b.percentage}%
                            </span>
                        ))}
                    </div>
                </div>
            )}

            <div className="mt-5 space-y-2" data-testid="simulator-breakdown">
                {!hasBreakdown ? (
                    <div className="profit-simulator__empty rounded-xl border border-dashed border-white/[0.1] bg-white/[0.015] px-4 py-8 text-center">
                        <Sparkles className="mx-auto h-5 w-5 text-white/25" />
                        <p className="mt-3 text-sm text-white/50">Enter an amount to preview the split</p>
                        <p className="mt-1 text-xs text-white/30">Uses current allocation percentages</p>
                    </div>
                ) : (
                    breakdown.map((b) => (
                        <div
                            key={b.member_id}
                            className="profit-simulator__row flex items-center justify-between gap-3 rounded-xl border border-white/[0.06] bg-white/[0.025] px-3 py-2.5 sm:px-4"
                            style={{ borderLeftColor: `${b.color}88`, borderLeftWidth: "3px" }}
                            data-testid={`breakdown-row-${b.member_id}`}
                        >
                            <div className="flex min-w-0 items-center gap-3">
                                <span
                                    className="profit-simulator__dot h-3 w-3 shrink-0 rounded-full"
                                    style={{
                                        backgroundColor: b.color,
                                        boxShadow: `0 0 10px ${b.color}88`,
                                    }}
                                />
                                <div className="min-w-0">
                                    <p className="truncate text-sm font-medium text-white/90">{b.name}</p>
                                    <p className="truncate text-[11px] text-white/40">
                                        {b.percentage}%
                                        {b.role_label ? ` · ${b.role_label}` : ""}
                                        {b.is_owner ? " · Owner" : ""}
                                    </p>
                                </div>
                            </div>
                            <p
                                className="shrink-0 font-mono text-sm font-semibold tabular-nums sm:text-base"
                                style={{ color: b.color }}
                            >
                                {formatCurrency(b.amount, currency, { decimals: 2 })}
                            </p>
                        </div>
                    ))
                )}
            </div>

            {hasBreakdown && (
                <div className="profit-simulator__total mt-4 flex items-center justify-between rounded-xl border border-[#00BF0D]/20 bg-[#00BF0D]/[0.04] px-4 py-3">
                    <span className="text-sm text-white/55">Total distributed</span>
                    <span className="font-mono text-base font-semibold text-[#00BF0D] tabular-nums sm:text-lg">
                        {formatCurrency(total, currency, { decimals: 2 })}
                    </span>
                </div>
            )}
        </section>
    );
}
